import { Badge, Button, Card, CardContent } from "@devhop/ui";
import { useParams, useRouter } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatKpiValueByUnit } from "@/modules/performance-tracking/presentation/lib/formatKpiValue";
import { labelKpiTargetUnit } from "@/modules/training-standard/presentation/lib/kpiTargetUnitLao";
import {
  labelKpiMeasurementPeriod,
  labelKpiResultStatus,
} from "@/modules/training-standard/presentation/lib/trainingLaoLabels";
import { formatDateLocal } from "@/shared/lib/date-time";
import { usePerformanceMeKpiDetailQuery } from "../api/queries";

function labelKpiScope(s: "personal" | "department"): string {
  return s === "department" ? "ຜະແນກ" : "ສ່ວນຕົວ";
}

function labelAssignmentType(
  t: "employee" | "position" | "department",
): string {
  if (t === "employee") return "ພະນັກງານ";
  if (t === "position") return "ຕຳແໜ່ງ";
  return "ຜະແນກ";
}

function labelKpiLevel(
  l: "employee" | "position" | "department" | "organization",
): string {
  if (l === "employee") return "ພະນັກງານ";
  if (l === "position") return "ຕຳແໜ່ງ";
  if (l === "department") return "ຜະແນກ";
  return "ອົງກອນ";
}

export function PerformanceKpiDetailPage() {
  const { assignmentId } = useParams({
    from: "/app/performance/kpi/$assignmentId",
  });
  const router = useRouter();
  const { data, isLoading, isError } =
    usePerformanceMeKpiDetailQuery(assignmentId);
  const k = data?.data;

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">
              {isLoading ? "…" : (k?.name ?? "KPI")}
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ລາຍລະອຽດຕົວຊີ້ວັດ ແລະ ການມອບໝາຍຕໍ່ທ່ານ
            </p>
          </div>

          <Button
            variant="ghost"
            className="mb-2 -ml-2 h-auto px-2"
            onClick={() => {
              router.history.back();
            }}
          >
            <ArrowLeft className="mr-2 size-4" />
            ກັບໄປລາຍການ KPI
          </Button>
        </div>

        {isLoading ? (
          <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>
        ) : isError || !k ? (
          <p className="text-muted-foreground">ບໍ່ພົບ KPI ຫຼື ທ່ານບໍ່ມີສິດເບິ່ງລາຍການນີ້</p>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">
                {labelKpiScope(k.kpiScope)} ·{" "}
                {labelAssignmentType(k.assignmentType)}
              </Badge>
              <Badge variant="outline">
                ລະດັບຕົວຊີ້ວັດ: {labelKpiLevel(k.kpiLevel)}
              </Badge>
              <Badge variant="outline">
                {labelKpiMeasurementPeriod(k.measurementPeriod)}
              </Badge>
            </div>

            <Card>
              <CardContent className="space-y-4 p-6 py-6">
                <h2 className="font-semibold text-base">ນິຍາມ ແລະ ວິທີວັດ</h2>
                {k.description ? (
                  <p className="whitespace-pre-line text-muted-foreground text-sm">
                    {k.description}
                  </p>
                ) : null}
                <dl className="grid gap-3 text-sm sm:grid-cols-1">
                  <div>
                    <dt className="font-medium text-muted-foreground">ວິທີວັດ</dt>
                    <dd className="mt-0.5 whitespace-pre-line">
                      {k.measurementMethod}
                    </dd>
                  </div>
                  {k.measurementSource ? (
                    <div>
                      <dt className="font-medium text-muted-foreground">
                        ແຫຼ່ງຂໍ້ມູນ
                      </dt>
                      <dd className="mt-0.5">{k.measurementSource}</dd>
                    </div>
                  ) : null}
                  {k.passCriteria ? (
                    <div>
                      <dt className="font-medium text-muted-foreground">
                        ເກນຜ່ານ
                      </dt>
                      <dd className="mt-0.5 whitespace-pre-line">
                        {k.passCriteria}
                      </dd>
                    </div>
                  ) : null}
                  {k.failCriteria ? (
                    <div>
                      <dt className="font-medium text-muted-foreground">
                        ເກນບໍ່ຜ່ານ
                      </dt>
                      <dd className="mt-0.5 whitespace-pre-line">
                        {k.failCriteria}
                      </dd>
                    </div>
                  ) : null}
                  {k.notes ? (
                    <div>
                      <dt className="font-medium text-muted-foreground">
                        ໝາຍເຫດ
                      </dt>
                      <dd className="mt-0.5 whitespace-pre-line">{k.notes}</dd>
                    </div>
                  ) : null}
                </dl>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="space-y-3 p-6 py-6">
                <h2 className="font-semibold text-base">ເປົ້າ ແລະ ຊ່ວງມອບໝາຍ</h2>
                <dl className="grid gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-muted-foreground">ເປົ້າໝາຍ</dt>
                    <dd className="font-medium">
                      {formatKpiValueByUnit(k.targetValue, k.targetUnit)}
                      {k.targetUnit && k.targetUnit !== "none"
                        ? ` ${labelKpiTargetUnit(k.targetUnit) || k.targetUnit}`
                        : ""}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">ຄ່າຈິງຫຼ້າສຸດ</dt>
                    <dd className="font-medium tabular-nums">
                      {formatKpiValueByUnit(k.currentValue, k.targetUnit)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">ສະຖານະຫຼ້າສຸດ</dt>
                    <dd>
                      <Badge variant="outline">
                        {labelKpiResultStatus(k.currentStatus)}
                      </Badge>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">ວັດແທກຫຼ້າສຸດ</dt>
                    <dd>
                      {k.measuredAt ? formatDateLocal(k.measuredAt) : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">ເລີ່ມມອບໝາຍ</dt>
                    <dd>{formatDateLocal(k.startDate)}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">ສິ້ນສຸດມອບໝາຍ</dt>
                    <dd>{k.endDate ? formatDateLocal(k.endDate) : "ຕໍ່ເນື່ອງ"}</dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            {k.periodResults.length > 0 ? (
              <section>
                <h2 className="mb-3 font-semibold text-lg">ຜົນຕາມຮອບ</h2>
                <div className="overflow-x-auto rounded-xl border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/40 text-left">
                        <th className="p-3 font-medium">ຮອບ</th>
                        <th className="p-3 font-medium">ຄ່າຈິງ</th>
                        <th className="p-3 font-medium">ເປົ້າ (ບັນທຶກ)</th>
                        <th className="p-3 font-medium">ສະຖານະ</th>
                        <th className="p-3 font-medium">ວັດແທກ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {k.periodResults.map((p) => {
                        const monthPart =
                          p.periodMonth != null
                            ? `/${String(p.periodMonth).padStart(2, "0")}`
                            : "";
                        const qPart =
                          p.periodQuarter != null ? ` Q${p.periodQuarter}` : "";
                        const periodLabel =
                          p.periodMonth != null
                            ? `${p.periodYear}${monthPart}`
                            : `${p.periodYear}${qPart}`;
                        return (
                          <tr
                            key={`${k.assignmentId}-${periodLabel}-${p.measuredAt}`}
                            className="border-b last:border-0"
                          >
                            <td className="p-3 tabular-nums">{periodLabel}</td>
                            <td className="p-3 tabular-nums">
                              {formatKpiValueByUnit(
                                p.actualValue,
                                k.targetUnit,
                              )}
                            </td>
                            <td className="p-3 tabular-nums">
                              {formatKpiValueByUnit(
                                p.targetValue,
                                k.targetUnit,
                              )}
                            </td>
                            <td className="p-3">
                              <Badge variant="outline">
                                {labelKpiResultStatus(p.status)}
                              </Badge>
                            </td>
                            <td className="p-3 text-muted-foreground">
                              {formatDateLocal(p.measuredAt)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : (
              <p className="text-muted-foreground text-sm">
                ຍັງບໍ່ມີຜົນຕາມຮອບໃນລະບົບສຳລັບມອບໝາຍນີ້
              </p>
            )}
          </div>
        )}
      </Main>
    </>
  );
}
